import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Tooltip from 'antd/lib/tooltip';

import { t } from 'utils/i18n';
import AlignOverlay from './align-overlay';
import {
    Matrix, Placement, placementFromPoints, Point, Size, startPlacement, toDrawing, toMatrix, wrapAngle,
} from './align-transform';
import {
    AlignItem, AlignState, alignedUrl, applyAlign, discardAlign, drawingSources, fetchAlign, startAlign,
} from './steps-api';

interface Props {
    taskId: number;
    saveChanges(): Promise<void>;
    onClose(replaced: boolean): void;
}

type View = 'before' | 'after';

// a drawing being aligned by hand; the placement is known once its image has loaded
interface Adjusting {
    frame: number;
    drawing: Size | null;
    initial: Placement | null;
    placement: Placement | null;
    // the placements before and after the one shown: Ctrl+Z and Ctrl+Shift+Z go through them
    past: Placement[];
    future: Placement[];
    // aligning by two points: those put on the first drawing (its pixels) and on this one (its own)
    points: { base: Point[]; over: Point[] } | null;
    // the two points put on a drawing lay on each other
    pointsTooClose?: boolean;
}

// what to click next, by the number of points put
const PICK_STEPS = [
    'Point 1: click on the blue drawing a spot both drawings share, a corner of a shaft or of an outer wall',
    'Click the same point 1 on the red drawing',
    'Point 2 on the blue drawing, as far from point 1 as you can: the farther, the more precise',
    'Click the same point 2 on the red drawing',
];

const POLL_INTERVAL = 1500;
const STATUS_LABELS: Record<AlignItem['status'], string> = {
    base: 'first',
    pending: 'waiting',
    aligned: 'aligned',
    manual: 'by hand',
    replaced: 'aligned before',
    failed: 'not aligned',
};

// aligned in this run: Replace replaces it
const isAligned = (item: AlignItem): boolean => item.status === 'aligned' || item.status === 'manual';
// on the first drawing, in this run or replaced before: the drawings after it are aligned to it
const liesOnFirst = (item: AlignItem): boolean => isAligned(item) || item.status === 'replaced';

// what a drawing is aligned to: the nearest drawing before it that is aligned itself, or the first
export function referenceOf(items: AlignItem[], frame: number): AlignItem | undefined {
    return items
        .filter((item) => item.frame < frame && (item.status === 'base' || liesOnFirst(item)))
        .reduce<AlignItem | undefined>((last, item) => (!last || item.frame > last.frame ? item : last), undefined);
}

// the server's messages are in English, with the numbers after a colon
function localizeError(message: string): string {
    return t(message.replace(/: \d+$/, ''));
}

// a confirmation is open over the review: its keys are its own
const modalOpen = (): boolean => !!document.querySelector('.ant-modal-confirm');

function messageOf(error: unknown): string {
    return localizeError(error instanceof Error ? error.message : String(error));
}

export default function AlignReview({ taskId, saveChanges, onClose }: Props): JSX.Element {
    const [state, setState] = useState<AlignState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [replacing, setReplacing] = useState(false);
    const [view, setView] = useState<View>('after');
    const [shown, setShown] = useState<number | null>(null);
    const [adjusting, setAdjusting] = useState<Adjusting | null>(null);
    // adjusting: the drawing is hidden for a moment, to see the lines under it
    const [hidden, setHidden] = useState(false);
    // the polling has its own error: a good answer clears it, but not the error of what the user did
    const [pollError, setPollError] = useState<string | null>(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    // bumped by every request of the user: an answer to a poll sent before it is out of date
    const epoch = useRef(0);

    // resolves to whether the action went through
    const attempt = useCallback(async (action: () => Promise<void>): Promise<boolean> => {
        epoch.current += 1;
        setBusy(true);
        setError(null);
        try {
            await action();
            return true;
        } catch (e) {
            if (mounted.current) setError(messageOf(e));
            return false;
        } finally {
            if (mounted.current) setBusy(false);
        }
    }, []);

    const update = useCallback((next: AlignState): void => {
        if (mounted.current) setState(next);
    }, []);

    const align = useCallback((manual: Record<number, Matrix | null> = {}) => attempt(async () => {
        update(await startAlign(taskId, manual));
    }), [taskId, attempt, update]);

    // a run in progress is joined; otherwise a new one checks what was left from before: the drawings
    // that have not changed keep how they were aligned
    const open = useCallback(() => attempt(async () => {
        const loaded = await fetchAlign(taskId);
        update(loaded);
        if (loaded.status !== 'running' && loaded.items.length > 1) update(await startAlign(taskId));
    }), [taskId, attempt, update]);
    useEffect(() => { open(); }, [open]);

    const running = state?.status === 'running';
    // one request at a time: a slow answer cannot come after a newer one
    useEffect(() => {
        if (!running) return undefined;
        let timer: ReturnType<typeof setTimeout>;
        let stopped = false;
        const poll = (): void => {
            timer = setTimeout(() => {
                const sent = epoch.current;
                fetchAlign(taskId)
                    .then((next) => {
                        if (stopped || sent !== epoch.current) return;
                        setState(next);
                        setPollError(null);
                    })
                    .catch((e) => !stopped && setPollError(messageOf(e)))
                    .finally(() => !stopped && poll());
            }, POLL_INTERVAL);
        };
        poll();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, [running, taskId]);

    const items = state?.items ?? [];
    const others = items.slice(1);
    const failed = others.filter((item) => item.status === 'failed');
    const replaced = others.filter(isAligned);
    const finished = state?.status === 'done';
    // a drawing can be adjusted once everything before it is aligned, even while the rest still runs
    const adjustable = (item: AlignItem): boolean => !busy && !adjusting && item.status !== 'pending' &&
        item.status !== 'base' && items.every((other) => other.frame >= item.frame || other.status !== 'pending');
    const current = others.find((item) => item.frame === (adjusting?.frame ?? shown)) ??
        others.find((item) => item.status === 'failed') ?? others[0];
    const reference = current && referenceOf(items, current.frame);
    const processed = others.filter((item) => item.status !== 'pending').length;
    let replaceHint: string | null = null;
    if (running) replaceHint = t('Available when the alignment finishes');

    const startAdjusting = (item: AlignItem): void => {
        setShown(item.frame);
        setHidden(false);
        setAdjusting({
            frame: item.frame, drawing: null, initial: null, placement: null, past: [], future: [], points: null,
        });
    };

    // the placement goes to the history before it changes: begin() before the overlay's gestures,
    // place() with a change made here
    const begin = (): void => setAdjusting((prev) => (prev?.placement ? {
        ...prev, past: [...prev.past, prev.placement], future: [],
    } : prev));
    const place = (next: Placement, rest: Partial<Adjusting> = {}): void => setAdjusting((prev) => (prev?.placement ? {
        ...prev, ...rest, past: [...prev.past, prev.placement], future: [], placement: next,
    } : prev));
    const undo = (): void => setAdjusting((prev) => (prev?.placement && prev.past.length ? {
        ...prev,
        placement: prev.past[prev.past.length - 1],
        past: prev.past.slice(0, -1),
        future: [prev.placement, ...prev.future],
    } : prev));
    const redo = (): void => setAdjusting((prev) => (prev?.placement && prev.future.length ? {
        ...prev, placement: prev.future[0], past: [...prev.past, prev.placement], future: prev.future.slice(1),
    } : prev));

    // the placement stays when the request fails: it is not to be done again
    const finishAdjusting = (frame: number, matrix: Matrix | null): void => {
        align({ [frame]: matrix }).then((done) => done && mounted.current && setAdjusting(null));
    };

    const turnBy = (degrees: number): void => {
        if (adjusting?.placement) {
            const { placement } = adjusting;
            place({ ...placement, angle: wrapAngle(placement.angle + degrees) });
        }
    };

    const cancel = (): void => {
        const close = (): void => {
            discardAlign(taskId).catch(() => {}).finally(() => onClose(false));
        };
        if (others.some((item) => item.status === 'manual')) {
            Modal.confirm({
                title: t('Discard the alignment?'),
                content: t('The drawings aligned by hand will have to be aligned again.'),
                okText: t('Discard'),
                okButtonProps: { danger: true },
                cancelText: t('Keep aligning'),
                onOk: close,
            });
        } else {
            close();
        }
    };

    const replace = (): void => {
        const run = (): void => {
            setReplacing(true);
            attempt(async () => {
                await saveChanges();
                // the result the user looked at: another one from another tab is not applied
                await applyAlign(taskId, state?.run ?? null);
                onClose(true);
            }).finally(() => mounted.current && setReplacing(false));
        };
        if (!failed.length) {
            run();
            return;
        }
        // the drawings that could not be aligned stay as they are
        Modal.confirm({
            title: t('Replace the aligned drawings?'),
            content: t('Not aligned, these stay as they are: {names}. You can align them another time.', {
                names: failed.map((item) => item.name).join(', '),
            }),
            okText: t('Replace'),
            cancelText: t('Cancel'),
            onOk: run,
        });
    };

    // the arrows go through the drawings, Space flips Before and After to see what moved; adjusting,
    // Space hides the drawing for a moment and Esc stops (the arrows nudge it, see the overlay)
    const keys = useRef({ others, current, adjusting });
    keys.current = { others, current, adjusting };
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            // the window itself when nothing has the focus
            const target = event.target instanceof HTMLElement ? event.target : document.body;
            const { others: list, current: item, adjusting: adjusted } = keys.current;
            const typing = target.tagName === 'TEXTAREA' || target.getAttribute('role') === 'combobox' ||
                (target.tagName === 'INPUT' && !['radio', 'checkbox'].includes((target as HTMLInputElement).type));
            if (typing || modalOpen()) return;
            if (adjusted) {
                if ((event.ctrlKey || event.metaKey) && ['z', 'Z', 'y'].includes(event.key) && !adjusted.points) {
                    event.preventDefault();
                    if (event.shiftKey || event.key === 'y') redo();
                    else undo();
                    return;
                }
                // Esc leaves putting points first, then adjusting
                if (event.key === 'Escape') {
                    setAdjusting((prev) => (prev?.points ? { ...prev, points: null } : null));
                }
                if (event.key === ' ' && target.tagName !== 'BUTTON' && !adjusted.points) {
                    event.preventDefault();
                    setHidden((prev) => !prev);
                }
                return;
            }
            if (event.key === ' ' && target.tagName !== 'BUTTON') {
                event.preventDefault();
                setView((prev) => (prev === 'after' ? 'before' : 'after'));
            } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && item) {
                event.preventDefault();
                const index = list.indexOf(item) + (event.key === 'ArrowDown' ? 1 : -1);
                if (index >= 0 && index < list.length) setShown(list[index].frame);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // the drawing chosen with the arrows stays in view in a long list
    useEffect(() => {
        document.querySelector('.cvat-align-review-item-current')?.scrollIntoView?.({ block: 'nearest' });
    }, [current?.frame]);

    const source = (item: AlignItem, after: boolean): string[] => (
        after && isAligned(item) ?
            [alignedUrl(taskId, item.frame, item.version)] : drawingSources(taskId, item.frame, item.version)
    );

    // aligned, a drawing lies on the first one, at its size; as it is, at its own
    const canvasOf = (item: AlignItem, after: boolean): Size => (after && item.status !== 'base' ? items[0] : item);

    const adjustButton = (item: AlignItem, primary = false): JSX.Element => (
        <Button
            type={primary ? 'primary' : 'link'}
            size='small'
            className='cvat-align-review-adjust'
            disabled={!adjustable(item)}
            onClick={(event) => {
                event.stopPropagation();
                startAdjusting(item);
            }}
        >
            {t('Adjust by hand')}
        </Button>
    );

    let canvas: React.ReactNode = null;
    if (adjusting && current && reference) {
        const { placement, drawing, points } = adjusting;
        const putting = !!points && !!placement && !!drawing;
        const put = points ? points.base.length + points.over.length : 0;
        const pick = (point: Point): void => {
            if (!points || !placement || !drawing) return;
            const next = put % 2 === 0 ?
                { ...points, base: [...points.base, point] } :
                { ...points, over: [...points.over, toDrawing(placement, drawing, point)] };
            if (next.over.length < 2) {
                setAdjusting({ ...adjusting, points: next, pointsTooClose: false });
                return;
            }
            const found = placementFromPoints(
                drawing, next.over as [Point, Point], next.base as [Point, Point],
            );
            if (found) place(found, { points: null });
            else setAdjusting({ ...adjusting, points: { base: [], over: [] }, pointsTooClose: true });
        };
        const back = (): void => {
            if (!points) return;
            setAdjusting({
                ...adjusting,
                points: put % 2 === 1 ?
                    { ...points, base: points.base.slice(0, -1) } : { ...points, over: points.over.slice(0, -1) },
            });
        };
        const bar = putting ? (
            <div className='cvat-align-review-adjust-bar'>
                <strong>{t(PICK_STEPS[put])}</strong>
                {adjusting.pointsTooClose && (
                    <span className='cvat-align-review-error'>
                        {t('The two points lay on each other: put them apart')}
                    </span>
                )}
                <span className='cvat-align-review-spacer' />
                <Button size='small' disabled={!put} onClick={back}>{t('Back')}</Button>
                <Button size='small' onClick={() => setAdjusting({ ...adjusting, points: null })}>{t('Cancel')}</Button>
            </div>
        ) : null;
        canvas = (
            <>
                {bar}
                <div className='cvat-align-review-adjust-bar' hidden={putting}>
                    <strong>{t('Aligning {name} by hand', { name: current.name })}</strong>
                    <span className='cvat-align-review-spacer' />
                    <Button
                        size='small'
                        disabled={!placement || !drawing}
                        onClick={() => setAdjusting({
                            ...adjusting, points: { base: [], over: [] }, pointsTooClose: false,
                        })}
                    >
                        {t('By two points')}
                    </Button>
                    <Button size='small' disabled={!placement} onClick={() => turnBy(-90)}>−90°</Button>
                    <Button size='small' disabled={!placement} onClick={() => turnBy(90)}>+90°</Button>
                    {t('Turn, °')}
                    <InputNumber
                        size='small'
                        step={0.1}
                        precision={2}
                        disabled={!placement}
                        value={placement ? Number(placement.angle.toFixed(2)) : undefined}
                        onChange={(value) => placement && value !== null && place({
                            ...placement, angle: wrapAngle(Number(value)),
                        })}
                        aria-label={t('Turn, °')}
                    />
                    {t('Scale, %')}
                    <InputNumber
                        size='small'
                        step={0.1}
                        precision={2}
                        min={1}
                        disabled={!placement}
                        value={placement ? Number((placement.scale * 100).toFixed(2)) : undefined}
                        onChange={(value) => placement && value && place({ ...placement, scale: Number(value) / 100 })}
                        aria-label={t('Scale, %')}
                    />
                    <Button
                        size='small'
                        disabled={!adjusting.initial}
                        onClick={() => adjusting.initial && place(adjusting.initial)}
                    >
                        {t('Reset')}
                    </Button>
                    {current.status === 'manual' && (
                        <Button size='small' disabled={busy} onClick={() => finishAdjusting(current.frame, null)}>
                            {t('Align automatically')}
                        </Button>
                    )}
                    <Button size='small' onClick={() => setAdjusting(null)}>{t('Cancel')}</Button>
                    <Button
                        size='small'
                        type='primary'
                        disabled={!placement || !adjusting.drawing || busy}
                        onClick={() => placement && adjusting.drawing &&
                            finishAdjusting(current.frame, toMatrix(placement, adjusting.drawing))}
                    >
                        {t('Apply')}
                    </Button>
                </div>
                <AlignOverlay
                    key={`adjust-${current.frame}`}
                    base={source(reference, true)}
                    over={drawingSources(taskId, current.frame, current.version)}
                    baseSize={canvasOf(reference, true)}
                    overSize={current}
                    baseName={reference.name}
                    overName={current.name}
                    adjust={{
                        placement,
                        hidden,
                        picking: putting && points && placement && drawing ? {
                            show: put % 2 === 0 ? 'base' : 'over',
                            marks: [
                                ...points.base.map((point, index) => ({
                                    ...point, label: String(index + 1), on: 'base' as const,
                                })),
                                // where the drawing as it lies now shows them
                                ...points.over.map((point, index) => {
                                    const [[a, c, e], [b, d, f]] = toMatrix(placement, drawing);
                                    return {
                                        x: a * point.x + c * point.y + e,
                                        y: b * point.x + d * point.y + f,
                                        label: String(index + 1),
                                        on: 'over' as const,
                                    };
                                }),
                            ],
                            onPick: pick,
                        } : undefined,
                        onLoad: (size) => {
                            // where it was aligned, else where the drawing before it was
                            const initial = startPlacement(size, current.matrix, reference.matrix);
                            setAdjusting((prev) => prev && {
                                ...prev, drawing: size, initial, placement: initial,
                            });
                        },
                        onBegin: begin,
                        onChange: (next) => setAdjusting((prev) => prev && { ...prev, placement: next }),
                    }}
                />
            </>
        );
    } else if (current && reference && (view === 'before' || liesOnFirst(current))) {
        canvas = (
            <AlignOverlay
                key='view'
                base={source(reference, view === 'after')}
                over={source(current, view === 'after')}
                baseSize={canvasOf(reference, view === 'after')}
                overSize={view === 'after' ? items[0] : current}
                baseName={reference.name}
                overName={current.name}
            />
        );
    } else if (current?.status === 'failed') {
        canvas = (
            <div className='cvat-align-review-placeholder'>
                {t('This drawing could not be aligned automatically')}
                {adjustButton(current, true)}
            </div>
        );
    } else if (current?.status === 'pending' && running) {
        canvas = <div className='cvat-align-review-placeholder'><Spin tip={t('Aligning…')} /></div>;
    }

    return (
        <Modal
            visible
            closable={false}
            maskClosable={false}
            keyboard={false}
            footer={null}
            width='96vw'
            className='cvat-align-review'
            title={(
                <div className='cvat-align-review-header'>
                    <strong>{t('Align {n} drawings', { n: items.length })}</strong>
                    {!adjusting && (
                        <Radio.Group
                            size='small'
                            value={view}
                            onChange={(event) => {
                                setView(event.target.value);
                                // the arrows and Space go on to the drawings, not to the radio
                                (document.activeElement as HTMLElement | null)?.blur();
                            }}
                            optionType='button'
                            options={[{ value: 'before', label: t('Before') }, { value: 'after', label: t('After') }]}
                        />
                    )}
                    <span className='cvat-align-review-spacer' />
                    {/* the result stays: opened again, what has not changed is kept */}
                    <Button disabled={replacing} onClick={() => onClose(false)}>{t('Close')}</Button>
                    {/* not while adjusting (the adjust bar has its own Cancel) nor while a request is on its way */}
                    <Button disabled={replacing || busy || !!adjusting} onClick={cancel}>
                        {t('Discard alignment')}
                    </Button>
                    <Tooltip title={replaceHint}>
                        <span>
                            <Button
                                type='primary'
                                className='cvat-align-review-replace'
                                disabled={!finished || busy || !replaced.length || !!adjusting}
                                loading={replacing}
                                onClick={replace}
                            >
                                {t('Replace {n} drawings', { n: replaced.length })}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            )}
        >
            {(error ?? pollError) && (
                <Alert
                    type='error'
                    showIcon
                    className='cvat-align-review-alert'
                    message={error ?? pollError}
                    // nothing was loaded or started: there is nothing else to retry from
                    action={(!state || state.status === 'none') && !busy ? (
                        <Button size='small' onClick={() => open()}>{t('Try again')}</Button>
                    ) : undefined}
                />
            )}
            {state?.status === 'failed' && (
                <Alert
                    type='error'
                    showIcon
                    className='cvat-align-review-alert'
                    message={state.error ? localizeError(state.error) : t('Could not align')}
                    action={(
                        <Button size='small' disabled={busy} onClick={() => align()}>{t('Try again')}</Button>
                    )}
                />
            )}
            {finished && failed.length > 0 && !adjusting && (
                <Alert
                    type='warning'
                    showIcon
                    className='cvat-align-review-alert'
                    message={t(
                        '{n} drawings could not be aligned automatically. Align them by hand, or replace the others and leave these as they are.',
                        { n: failed.length },
                    )}
                    action={adjustButton(failed[0], true)}
                />
            )}
            {state && items.length < 2 && (
                <Alert type='info' showIcon message={t('There is nothing to align: the task has one drawing')} />
            )}
            {!state && <Spin />}
            {state && items.length > 1 && (
                <div className='cvat-align-review-body'>
                    <div className='cvat-align-review-list'>
                        {running && (
                            <div className='cvat-align-review-progress'>
                                <Spin size='small' />
                                {t('Aligning… {done} of {total}', { done: processed, total: others.length })}
                            </div>
                        )}
                        {items.map((item) => (
                            <div
                                key={item.frame}
                                role='button'
                                tabIndex={item.status === 'base' ? -1 : 0}
                                aria-disabled={item.status === 'base' || !!adjusting}
                                className={`cvat-align-review-item cvat-align-review-item-${item.status}${
                                    current?.frame === item.frame ? ' cvat-align-review-item-current' : ''}`}
                                onClick={() => item.status !== 'base' && !adjusting && setShown(item.frame)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && item.status !== 'base' && !adjusting) {
                                        setShown(item.frame);
                                    }
                                }}
                            >
                                <span className='cvat-align-review-name'>{item.name}</span>
                                <span className='cvat-align-review-status'>{t(STATUS_LABELS[item.status])}</span>
                                {item.error && (
                                    <span className='cvat-align-review-error'>{localizeError(item.error)}</span>
                                )}
                                {item.status !== 'base' && item.status !== 'pending' && adjustButton(item)}
                            </div>
                        ))}
                    </div>
                    <div className='cvat-align-review-canvas'>{canvas}</div>
                </div>
            )}
        </Modal>
    );
}
